// Team (WEB_PLAN.md §5.4, route /team): everyone the viewer can see, grouped by team, with their account status from
// `team_roster` (Signed up / Invited / Invitation expired / Suspended / No account), when they were last seen, the
// stores they own and this week's visits. Ports TeamView's roster (GroundTeam/Views/Team/TeamView.swift) and Team
// members (GroundTeam/Views/Sync/TeamMembersView.swift) into one table.
// Admins invite, share or revoke invitations and remove accounts; managers read (invitations without codes). Phase 1
// writes nothing else: profiles are edited on the iPhone until Phase 2.

import { CalendarDays, CircleX, Copy, Handshake, MapPin, RefreshCw, Ticket, UserCheck, UserPlus, UserRoundX, Users } from 'lucide-react';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '@/auth/AuthProvider';
import { teamErrorMessage } from '@/auth/errors';
import type { TeamInvitation } from '@/contracts/wire';
import { useMetricsIndex, useRecords, useRepPositions, useTeams, useUsers, useVisibleUsers } from '@/data/hooks';
import { resolveWeekStart } from '@/domain/calendar';
import { formatInviteCode } from '@/domain/team';
import { byId, idKey } from '@/domain/util';
import { RequireFirstSync, ScreenLoading, useDocumentTitle } from '@/features/common';
import { useCan } from '@/session/permissions';
import {
  Button,
  Card,
  ConfirmDialog,
  GT_COLORS,
  KpiTile,
  PageContainer,
  PageHeader,
  SearchField,
  SegmentedControl,
  useTicker,
  useToast,
  type MenuEntry,
} from '@/ui';
import { inviteErrorMessage, REMOVE_MEMBER_MESSAGE, revokeMessage } from './invite';
import { InviteDialog } from './InviteDialog';
import { ShareInvitationDialog, useCompanyName } from './InviteShareView';
import {
  DEFAULT_MEMBER_SORT,
  accountOf,
  groupMemberRows,
  indexRoster,
  memberActions,
  memberProfileIds,
  memberStats,
  peopleRows,
  searchPeople,
  teamSummary,
  type MemberSort,
  type PersonRow,
} from './members';
import { MembersTable } from './MembersTable';
import { removeMember, revokeInvitation, useTeamRoster } from './teamApi';

export default function TeamPage() {
  useDocumentTitle('Team');
  return (
    <PageContainer>
      <RequireFirstSync
        what="Team"
        fallback={
          <>
            <PageHeader title="Team" />
            <ScreenLoading layout="table" label="Loading team…" />
          </>
        }
      >
        <TeamBody />
      </RequireFirstSync>
    </PageContainer>
  );
}

type Grouping = 'team' | 'all';

interface InviteState {
  open: boolean;
  profileId: string | null;
}

function TeamBody() {
  const session = useSession();
  const can = useCan();
  const navigate = useNavigate();
  const toast = useToast();
  const companyName = useCompanyName();

  const visible = useVisibleUsers();
  const users = useUsers();
  const teams = useTeams();
  const records = useRecords();
  const index = useMetricsIndex();
  const positions = useRepPositions();
  const rosterQuery = useTeamRoster(can.canSeeRoster);
  const roster = rosterQuery.roster;

  // Presence and "this week" move with the clock, not only with the data.
  const tick = useTicker(60_000);
  const now = useMemo(() => new Date(), [tick]);
  const weekStartsOn = useMemo(() => resolveWeekStart(), []);

  // Every visible profile, those a newer app wrote included (best-effort values, flagged): `values` alone would drop
  // them from Team (an admin would then see them only as an account without a profile).
  const visibleProfiles = useMemo(() => visible.items.map((i) => i.value), [visible.items]);
  const newerAppIds = useMemo(
    () => new Set(visible.items.filter((i) => i.undecodable !== undefined).map((i) => idKey(i.id))),
    [visible.items],
  );

  const stats = useMemo(() => memberStats(index, records.values, now, weekStartsOn), [index, records.values, now, weekStartsOn]);
  const rows = useMemo(
    () =>
      peopleRows({
        users: visibleProfiles,
        newerAppIds,
        teams: teams.values,
        roster,
        includeOrphans: can.isAdmin,
        meId: session.profileId,
        positions,
        stats,
        now,
      }),
    [visibleProfiles, newerAppIds, teams.values, roster, can.isAdmin, session.profileId, positions, stats, now],
  );

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [grouping, setGrouping] = useState<Grouping>('team');
  const [sort, setSort] = useState<MemberSort>(DEFAULT_MEMBER_SORT);
  const usersById = useMemo(() => byId(users.values), [users.values]);
  const tableRows = useMemo(
    () => groupMemberRows(searchPeople(rows, deferredQuery), teams.values, usersById, { grouped: grouping === 'team', sort }),
    [rows, deferredQuery, teams.values, usersById, grouping, sort],
  );
  const summary = useMemo(() => teamSummary(rows, stats, roster, now), [rows, stats, roster, now]);

  const rosterIndex = useMemo(() => (roster ? indexRoster(roster) : null), [roster]);
  const accountOfProfile = useCallback(
    (profileId: string) => (rosterIndex ? accountOf(rosterIndex.get(profileId.toLowerCase()), now) : null),
    [rosterIndex, now],
  );
  const memberIds = useMemo(() => memberProfileIds(roster), [roster]);

  const [invite, setInvite] = useState<InviteState>({ open: false, profileId: null });
  const [share, setShare] = useState<{ invitation: TeamInvitation; name: string | null } | null>(null);
  const [revoke, setRevoke] = useState<{ invitation: TeamInvitation; name: string } | null>(null);
  const [remove, setRemove] = useState<PersonRow | null>(null);

  const copyCode = useCallback(
    async (code: string) => {
      try {
        await navigator.clipboard.writeText(formatInviteCode(code));
        toast.success('Invite code copied');
      } catch {
        toast.error("Couldn't copy. Open the invitation and copy the code from there.");
      }
    },
    [toast],
  );

  // Which entries a row gets is members.ts `memberActions` (each follows its own server rule); this maps them to menu
  // items.
  const { canInvite, canSeeInviteCodes, canRevokeInvitations, canRemoveMembers } = can;
  const hasRowActions = canInvite || canRevokeInvitations || canRemoveMembers;
  const actionsFor = useCallback(
    (r: PersonRow): MenuEntry[] => {
      const kinds = memberActions(r, { canInvite, canSeeInviteCodes, canRevokeInvitations, canRemoveMembers });
      const invitation = r.account?.invitation;
      const items: MenuEntry[] = [];
      for (const kind of kinds) {
        // Destructive entries sit below a separator, as in the iOS context menus.
        if ((kind === 'revoke' || kind === 'remove') && items.length > 0 && items[items.length - 1]?.type !== 'separator') {
          items.push({ type: 'separator', id: `sep-${kind}` });
        }
        switch (kind) {
          case 'open':
            items.push({ id: 'open', label: 'View day', icon: <CalendarDays />, onSelect: () => navigate(`/team/${r.profileId}`) });
            break;
          case 'showCode':
            if (invitation) {
              items.push({
                id: 'show',
                label: 'Show invite code',
                icon: <Ticket />,
                onSelect: () => setShare({ invitation, name: r.user ? r.name : null }),
              });
            }
            break;
          case 'copyCode': {
            const code = invitation?.code;
            if (code) items.push({ id: 'copy', label: 'Copy invite code', icon: <Copy />, onSelect: () => void copyCode(code) });
            break;
          }
          case 'inviteAgain':
          case 'invite':
            items.push({
              id: kind,
              label: kind === 'invite' ? 'Invite…' : 'Invite again…',
              icon: <UserPlus />,
              onSelect: () => setInvite({ open: true, profileId: r.profileId }),
            });
            break;
          case 'revoke':
            if (invitation) {
              items.push({
                id: 'revoke',
                label: 'Revoke invitation',
                icon: <CircleX />,
                destructive: true,
                onSelect: () => setRevoke({ invitation, name: r.name }),
              });
            }
            break;
          case 'remove':
            items.push({
              id: 'remove',
              label: 'Remove from company',
              icon: <UserRoundX />,
              destructive: true,
              onSelect: () => setRemove(r),
            });
            break;
        }
      }
      return items;
    },
    [navigate, copyCode, canInvite, canSeeInviteCodes, canRevokeInvitations, canRemoveMembers],
  );

  const openPerson = useCallback(
    (r: PersonRow) => {
      if (r.profileId) navigate(`/team/${r.profileId}`);
    },
    [navigate],
  );

  const subtitle =
    summary.people === 1 ? `1 person · ${summary.online} online` : `${summary.people.toLocaleString()} people · ${summary.online} online`;

  return (
    <>
      <PageHeader
        title="Team"
        subtitle={subtitle}
        actions={
          can.canInvite ? (
            // Until the roster is in, the picker cannot leave out profiles that already have an account.
            <Button
              variant="primary"
              icon={<UserPlus />}
              disabled={rosterQuery.loading}
              title={rosterQuery.loading ? 'Loading accounts…' : undefined}
              onClick={() => setInvite({ open: true, profileId: null })}
            >
              Invite teammate
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiTile label="People" value={summary.people.toLocaleString()} sub={`${summary.online} online`} icon={<Users />} color={GT_COLORS.navy} />
        <KpiTile label="Visits today" value={summary.visitsToday.toLocaleString()} icon={<MapPin />} color={GT_COLORS.orange} />
        <KpiTile label="Contacts today" value={summary.contactsToday.toLocaleString()} icon={<Handshake />} color={GT_COLORS.teal} />
        <KpiTile
          label="Signed up"
          value={summary.signedUp === null ? '—' : summary.signedUp.toLocaleString()}
          sub={summary.signedUp === null ? undefined : `of ${summary.people.toLocaleString()} people`}
          loading={rosterQuery.loading}
          icon={<UserCheck />}
          color={GT_COLORS.green}
        />
        <KpiTile
          label="Pending invitations"
          value={summary.pendingInvitations === null ? '—' : summary.pendingInvitations.toLocaleString()}
          sub={summary.expiredInvitations ? `${summary.expiredInvitations} expired` : undefined}
          loading={rosterQuery.loading}
          icon={<Ticket />}
          color={GT_COLORS.amber}
        />
      </div>

      {rosterQuery.error != null && !roster && (
        <Card role="alert" padding="sm" className="mb-4 flex flex-wrap items-center justify-between gap-3 border-danger/40 bg-danger-soft">
          <p className="text-sm text-danger-text">
            Couldn't load accounts and invitations. {teamErrorMessage(rosterQuery.error, companyName)}
          </p>
          <Button size="sm" icon={<RefreshCw />} onClick={rosterQuery.refresh}>
            Try again
          </Button>
        </Card>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchField
          value={query}
          onValueChange={setQuery}
          label="Search people"
          placeholder="Search name, email, title or team"
          slashShortcut
          className="max-w-md min-w-[220px] flex-1"
        />
        <SegmentedControl<Grouping>
          label="Group people"
          size="sm"
          value={grouping}
          onChange={setGrouping}
          options={[
            { value: 'team', label: 'By team' },
            { value: 'all', label: 'Everyone' },
          ]}
        />
      </div>

      <MembersTable
        rows={tableRows}
        sort={sort}
        onSortChange={setSort}
        now={now}
        rosterLoading={rosterQuery.loading}
        onOpen={openPerson}
        actionsFor={hasRowActions ? actionsFor : undefined}
        grouped={grouping === 'team'}
        query={deferredQuery}
        onClearQuery={() => setQuery('')}
        className="h-[max(420px,calc(100dvh_-_26rem))]"
      />

      <p className="mt-3 px-1 text-xs text-fg-muted">
        {can.isAdmin
          ? 'Invitees create their own account and confirm before joining: the invitation shows up by itself when they sign up with the invited email, or they enter the invite code you share. Codes expire after 14 days. Everyone manages their own password.'
          : 'Only admins can invite or remove members. Invitees create their own account and confirm before joining, with the invited email or the invite code an admin shares.'}
      </p>

      <InviteDialog
        open={invite.open}
        initialProfileId={invite.profileId}
        memberProfileIds={memberIds}
        accountOf={accountOfProfile}
        onClose={() => setInvite({ open: false, profileId: null })}
        onInvited={() => rosterQuery.refresh()}
      />

      <ShareInvitationDialog invitation={share?.invitation ?? null} name={share?.name} onClose={() => setShare(null)} />

      <ConfirmDialog
        open={revoke !== null}
        onClose={() => setRevoke(null)}
        title="Revoke this invitation?"
        message={revokeMessage(revoke?.invitation)}
        confirmLabel="Revoke invitation"
        destructive
        onConfirm={async () => {
          if (!revoke) return;
          try {
            await revokeInvitation(revoke.invitation.id);
          } catch (error) {
            throw new Error(inviteErrorMessage(error, companyName));
          }
          rosterQuery.refresh();
          toast.success('Invitation revoked');
        }}
      />

      <ConfirmDialog
        open={remove !== null}
        onClose={() => setRemove(null)}
        title={remove ? `Remove ${remove.name || 'this member'}?` : 'Remove member?'}
        message={REMOVE_MEMBER_MESSAGE}
        confirmLabel="Remove from company"
        destructive
        onConfirm={async () => {
          const member = remove?.account?.member;
          if (!remove || !member) return;
          try {
            await removeMember(session.companyId, member.user_id);
          } catch (error) {
            throw new Error(inviteErrorMessage(error, companyName));
          }
          rosterQuery.refresh();
          toast.success(`${remove.name || 'The member'} was removed from the company.`);
        }}
      />
    </>
  );
}
